// AnalyticsManagement.js
import React, { useState, useEffect } from 'react';
import './AnalyticsManagement.css';
import api from '../../utils/axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const AnalyticsManagement = () => {
    const [activeUsersData, setActiveUsersData] = useState([]);
    const [earningsData, setEarningsData] = useState([]);
    const [audioFilesData, setAudioFilesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timePeriod, setTimePeriod] = useState('30d'); // Default to 30 days

    const [metrics, setMetrics] = useState({
        activeUsers: [],
        newUsers: [],
        earnings: [],
        subscriptions: [],
        customerSatisfaction: []
    });

    const [summaryStats, setSummaryStats] = useState({
        newSignups: 0,
        conversionRate: 0,
        churnRate: 0,
        retentionRate: 0,
        mrr: 0,
        arr: 0,
        arpu: 0,
        cltv: 0,
        nps: 0,
        activeUsers: 0,
        earnings: 0,
        recordings: 0
    });

    const timePeriods = [
        { value: '24h', label: 'Last 24 Hours' },
        { value: '7d', label: 'Last 7 Days' },
        { value: '30d', label: 'Last 30 Days' },
        { value: '1y', label: 'Last Year' }
    ];

    const calculatePeriodTotals = (data) => {
        return {
            activeUsers: parseFloat(
                data.activeUsers.reduce(
                    (sum, item) => sum + parseFloat(item.active_users || 0),
                    0
                ).toFixed(2)
            ),
            earnings: parseFloat(
                data.earnings.reduce(
                    (sum, item) => sum + parseFloat(item.total_earnings || 0),
                    0
                ).toFixed(2)
            ),
            recordings: parseFloat(
                data.recordings.reduce(
                    (sum, item) => sum + parseFloat(item.file_count || 0),
                    0
                ).toFixed(2)
            ),
            // Ensure newSignups and conversionRate are included
            newSignups: summaryStats.newSignups,
            conversionRate: summaryStats.conversionRate
        };
    };

    const formatCurrency = (value) => {
        // Convert to number and handle invalid/null values
        const num = Number(value);
        return !isNaN(num) ? `$${num.toFixed(2)}` : '$0.00';
    };

    const formatXAxis = (value) => {
        const date = new Date(value);
        if (timePeriod === '24h') {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [
                    usersRes,
                    earningsRes,
                    filesRes,
                    signupsRes,
                    conversionRes,
                    churnRes,
                    retentionRes
                ] = await Promise.all([
                    api.get(`/admin/analytics/active-users/${timePeriod}`),
                    api.get(`/admin/analytics/earnings/${timePeriod}`),
                    api.get(`/admin/analytics/audio-files/${timePeriod}`),
                    api.get(`/admin/analytics/new-signups/${timePeriod}`),
                    api.get(`/admin/analytics/conversion-rate/${timePeriod}`),
                    api.get(`/admin/analytics/churn-rate/${timePeriod}`),
                    api.get(`/admin/analytics/retention-rate/${timePeriod}`)
                ]);

                setActiveUsersData(usersRes.data);
                setEarningsData(earningsRes.data);
                setAudioFilesData(filesRes.data);

                // Update summaryStats with new data
                setSummaryStats({
                    ...calculatePeriodTotals({
                        activeUsers: usersRes.data,
                        earnings: earningsRes.data,
                        recordings: filesRes.data
                    }),
                    newSignups: signupsRes.data.newSignups,
                    conversionRate: conversionRes.data.conversionRate,
                    churnRate: churnRes.data.churnRate,
                    retentionRate: retentionRes.data.retentionRate
                });
            } catch (error) {
                setError('Failed to fetch analytics data');
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [timePeriod]);

    const getYAxisDomain = (data, key) => {
        if (!data || data.length === 0) return [0, 10];
        const maxValue = Math.max(...data.map(item => Number(item[key])));
        return [0, Math.ceil(maxValue * 1.1)]; // Add 10% padding
    };

    if (loading) return <div className="loading">Loading analytics...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="admin-page">
            <h1>Analytics Management</h1>

            <div className="time-period-selector">
                <select
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                    className="time-select"
                >
                    {timePeriods.map(period => (
                        <option key={period.value} value={period.value}>
                            {period.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* User Acquisition Section */}
            <section className="metrics-section">
                <h2>User Acquisition & Growth</h2>
                <div className="metrics-grid">
                    <MetricCard
                        title="New Sign-ups"
                        value={summaryStats.newSignups}
                        trend="+12%"
                    />
                    <MetricCard
                        title="Conversion Rate"
                        value={`${summaryStats.conversionRate}%`}
                        trend="+5%"
                    />
                </div>
                <section className="analytics-section">
                    <div className="section-header">
                        <h2>Active Sessions</h2>
                        <div className="period-total">
                            +{summaryStats.activeUsers} sessions this period
                        </div>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={activeUsersData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={formatXAxis} />
                                <YAxis domain={getYAxisDomain(activeUsersData, 'active_users')} />
                                <Tooltip labelFormatter={formatXAxis} />
                                <Legend />
                                <Line type="monotone" dataKey="active_users" stroke="#8884d8" name="Active Users" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            </section>

            {/* User Retention Section */}
            <section className="metrics-section">
                <h2>Engagement & Retention</h2>
                <div className="metrics-grid">
                    <MetricCard
                        title="Churn Rate"
                        value={`${summaryStats.churnRate}%`}
                        trend="-TBA%"
                    />
                    <MetricCard
                        title="Retention Rate"
                        value={`${summaryStats.retentionRate}%`}
                        trend="+TBA%"
                    />
                </div>
            </section>

            {/* Revenue Section */}
            <section className="metrics-section">
                <h2>Revenue Metrics</h2>
                <div className="metrics-grid">
                    <MetricCard
                        title="MRR"
                        value={formatCurrency(summaryStats.mrr)}
                        trend="+8%"
                    />
                    <MetricCard
                        title="ARR"
                        value={formatCurrency(summaryStats.arr)}
                        trend="+15%"
                    />
                    <MetricCard
                        title="ARPU"
                        value={formatCurrency(summaryStats.arpu)}
                        trend="+4%"
                    />
                    <MetricCard
                        title="CLTV"
                        value={formatCurrency(summaryStats.cltv)}
                        trend="+10%"
                    />
                </div>
                <section className="analytics-section">
                    <div className="section-header">
                        <h2>Earnings Over Time</h2>
                        <div className="period-total">
                            +{formatCurrency(summaryStats.earnings)} this period
                        </div>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={earningsData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={formatXAxis} />
                                <YAxis domain={getYAxisDomain(earningsData, 'total_earnings')} />
                                <Tooltip
                                    labelFormatter={formatXAxis}
                                    formatter={(value) => [formatCurrency(value), 'Earnings']}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="total_earnings" stroke="#82ca9d" name="Total Earnings" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            </section>

            {/* Customer Satisfaction Section */}
            <section className="metrics-section">
                <h2>Customer Satisfaction</h2>
                <div className="metrics-grid">
                    <MetricCard
                        title="NPS Score"
                        value={summaryStats.nps}
                        trend="+5"
                    />
                    <ChartContainer
                        title="NPS Trend"
                        data={metrics.customerSatisfaction}
                        dataKey="nps_score"
                        formatCurrency={formatCurrency}
                    />
                </div>
            </section>

            <section className="analytics-section">
                <div className="section-header">
                    <h2>Recording Activity</h2>
                    <div className="period-total">
                        +{summaryStats.recordings} recordings this period
                    </div>
                </div>
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={audioFilesData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickFormatter={formatXAxis} />
                            <YAxis domain={getYAxisDomain(audioFilesData, 'file_count')} />
                            <Tooltip labelFormatter={formatXAxis} />
                            <Legend />
                            <Line type="monotone" dataKey="file_count" stroke="#ff7300" name="Recordings" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>


        </div>
    );
};

const ChartContainer = ({ title, data, dataKey, isCurrency, formatCurrency }) => (
    <div className="chart-container">
        <h3>{title}</h3>
        <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={date => new Date(date).toLocaleDateString()} />
                <YAxis />
                <Tooltip
                    formatter={value => isCurrency ? formatCurrency(value) : value}
                />
                <Legend />
                <Line type="monotone" dataKey={dataKey} stroke="#8884d8" />
            </LineChart>
        </ResponsiveContainer>
    </div>
);

const MetricCard = ({ title, value, trend }) => (
    <div className="metric-card">
        <h4>{title}</h4>
        <div className="metric-value">{value}</div>
        <div className={`trend ${trend.startsWith('+') ? 'positive' : 'negative'}`}>
            {trend}
        </div>
    </div>
);